// src/contexts/AuthContext.js - OPTIMIZED WITH ENHANCED ERROR HANDLING
import { createContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiCall, checkHealth } from '../utils/apiUtils';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(null);
    const [loading, setLoading] = useState(true);
    const refreshPromiseRef = useRef(null);
    const isRefreshingRef = useRef(false);
    const lastHealthCheckRef = useRef(0);

    // ✅ NEW: Check service health before critical operations
    const ensureServiceHealth = useCallback(async () => {
        const now = Date.now();
        // Only check health once every 5 minutes to avoid Cloudflare rate limits
        if (now - lastHealthCheckRef.current < 5 * 60 * 1000) {
            return true;
        }

        try {
            const { healthy } = await checkHealth();
            lastHealthCheckRef.current = now;
            return healthy;
        } catch (error) {
            console.error('[ensureServiceHealth] Health check failed:', error);
            return false;
        }
    }, []);

    // ✅ ENHANCED: Refresh token with retry logic
    const refreshAccessToken = useCallback(async () => {
        if (isRefreshingRef.current && refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        isRefreshingRef.current = true;
        refreshPromiseRef.current = (async () => {
            let lastError = null;
            const maxAttempts = 3;

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    // console.log(`Attempting to refresh token (attempt ${attempt}/${maxAttempts})...`);

                    const data = await apiCall('/api/auth/refresh', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ refreshToken }),
                    });

                    // console.log('Token refresh successful');
                    localStorage.setItem('accessToken', data.accessToken);
                    if (data.refreshToken) {
                        localStorage.setItem('refreshToken', data.refreshToken);
                    }
                    return data.accessToken;
                } catch (error) {
                    console.error(`Token refresh attempt ${attempt} failed:`, error);
                    lastError = error;

                    // Check if it's a retryable error
                    const isRetryable = error.status === 503 ||
                        error.code === 'DB_NOT_CONNECTED' ||
                        error.code === 'DB_CONNECTION_LOST' ||
                        error.code === 'DB_ERROR';

                    if (isRetryable && attempt < maxAttempts) {
                        const delay = 2000 * attempt; // Exponential backoff
                        // console.log(`Service temporarily unavailable, retrying in ${delay}ms...`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                        continue;
                    }

                    // Non-retryable error or max attempts reached
                    break;
                }
            }

            // All attempts failed
            console.error('Token refresh failed after all attempts, logging out');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('rememberMe');
            setAuth(null);
            throw lastError;
        })();

        try {
            return await refreshPromiseRef.current;
        } finally {
            isRefreshingRef.current = false;
            refreshPromiseRef.current = null;
        }
    }, []);

    // ✅ ENHANCED: API call with better error handling
    const apiCallWithAuth = useCallback(
        async (url, options = {}, toast) => {
            let accessToken = localStorage.getItem('accessToken');
            if (!accessToken) throw new Error('No access token available');

            const makeRequest = async (token) => {
                return apiCall(
                    url,
                    {
                        ...options,
                        headers: {
                            ...options.headers,
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    },
                    toast
                );
            };

            try {
                return await makeRequest(accessToken);
            } catch (error) {
                // console.log('API call failed:', error.message);

                // Only try to refresh on token expired, not on service unavailable
                if (error.message === 'Token expired' || error.code === 'TOKEN_EXPIRED') {
                    try {
                        // console.log('Token expired, attempting refresh...');
                        accessToken = await refreshAccessToken();
                        return await makeRequest(accessToken);
                    } catch (refreshError) {
                        console.error('Token refresh failed, logging out');
                        setAuth(null);
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        localStorage.removeItem('rememberMe');
                        window.location.href = '/sign-in';
                        throw refreshError;
                    }
                }

                throw error;
            }
        },
        [refreshAccessToken]
    );

    // ✅ OPTIMIZATION: Memoize processPermissions function
    const processPermissions = useCallback((rawPermissions) => {
        if (!rawPermissions) {
            return {
                sidebar: [],
                features: {}
            };
        }

        const processedFeatures = {};
        if (rawPermissions.features) {
            Object.keys(rawPermissions.features).forEach(tabName => {
                if (rawPermissions.features[tabName] && typeof rawPermissions.features[tabName] === 'object') {
                    processedFeatures[tabName] = { ...rawPermissions.features[tabName] };
                }
            });
        }

        return {
            sidebar: Array.isArray(rawPermissions.sidebar) ? rawPermissions.sidebar : [],
            features: processedFeatures
        };
    }, []);

    // ✅ OPTIMIZATION: Memoize permission checking functions
    const hasPermission = useCallback((tabName, featureName) => {
        if (!auth?.permissions?.features?.[tabName]) {
            return false;
        }
        return Boolean(auth.permissions.features[tabName][featureName]);
    }, [auth?.permissions]);

    const canAccessTab = useCallback((tabName) => {
        if (!auth?.permissions?.sidebar) {
            return false;
        }
        return auth.permissions.sidebar.includes(tabName);
    }, [auth?.permissions]);

    const isManager = useCallback(async () => {
        if (!auth?.id) return false;

        try {
            const data = await apiCallWithAuth(`/api/users?assignedManager=${auth.id}&limit=1`);
            return data.users && data.users.length > 0;
        } catch (error) {
            console.error('Error checking manager status:', error);
            return false;
        }
    }, [auth?.id, apiCallWithAuth]);

    const getManagedUsers = useCallback(async () => {
        if (!auth?.id) return [];

        try {
            const data = await apiCallWithAuth(`/api/users?assignedManager=${auth.id}&limit=1000`);
            return data.users || [];
        } catch (error) {
            console.error('Error fetching managed users:', error);
            return [];
        }
    }, [auth?.id, apiCallWithAuth]);

    // ✅ NEW: Consume auth transfer cookie if it exists
    const consumeAuthTransfer = () => {
        const match = document.cookie.match(/(?:^|;\s*)auth_transfer=([^;]*)/);
        if (match && match[1]) {
            try {
                const data = JSON.parse(decodeURIComponent(match[1]));
                if (data.accessToken && data.refreshToken) {
                    localStorage.setItem('accessToken', data.accessToken);
                    localStorage.setItem('refreshToken', data.refreshToken);
                    if (data.companySlug) {
                        localStorage.setItem('companySlug', data.companySlug);
                    }
                }
            } catch (e) {
                console.error('Error parsing auth_transfer cookie', e);
            }
            // Delete cookie
            document.cookie = 'auth_transfer=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.empworkspace.com; path=/;';
        }
    };

    // ✅ ENHANCED: Auth check with better error handling
    const performAuthCheck = useCallback(
        async (isInitialCall = false) => {
            if (isInitialCall) setLoading(true);
            
            // Check for transfer cookie first and consume it
            consumeAuthTransfer();

            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            const rememberMe = localStorage.getItem('rememberMe') === 'true';

            if (!accessToken || !refreshToken) {
                setAuth(null);
                if (isInitialCall) setLoading(false);
                return;
            }

            try {
                // console.log('Performing auth check...');
                const userData = await apiCallWithAuth('/api/auth/me');

                const user = userData.user || userData;
                const processedPermissions = processPermissions(user.permissions);

                // Store companySlug in localStorage for API calls
                if (user.companySlug) {
                    localStorage.setItem('companySlug', user.companySlug);
                }

                const authState = {
                    id: user._id || user.id,
                    userId: user._id || user.id,
                    name: user.name,
                    email: user.email,
                    role: user.role,
                    position: user.position,
                    status: user.status,
                    employeeId: user.employeeId,
                    profileImage: user.profileImage,
                    cardBackground: user.cardBackground,
                    projects: user.projects,
                    awards: user.awards,
                    token: localStorage.getItem('accessToken'),
                    rememberMe,
                    companySlug: user.companySlug,
                    companyName: user.companyName,
                    permissions: processedPermissions,
                    managedUsers: user.managedUsers || [],
                    isManager: user.isManager || false,
                    termsAccepted: user.termsAccepted || false,
                    termsAcceptedAt: user.termsAcceptedAt,
                    termsVersion: user.termsVersion,
                    hasPermission: (tabName, featureName) => {
                        return Boolean(processedPermissions.features?.[tabName]?.[featureName]);
                    },
                    canAccessTab: (tabName) => {
                        return processedPermissions.sidebar.includes(tabName);
                    }
                };

                setAuth(authState);
                // console.log('Auth check successful');
            } catch (error) {
                console.error('Auth check error:', error);

                // Don't logout on service unavailable - let user stay logged in
                const isServiceError = error.status === 503 ||
                    error.code === 'DB_NOT_CONNECTED' ||
                    error.code === 'DB_CONNECTION_LOST' ||
                    error.code === 'DB_ERROR';

                if (!isServiceError) {
                    setAuth(null);
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('rememberMe');
                }
            }
            if (isInitialCall) setLoading(false);
        },
        [apiCallWithAuth, processPermissions]
    );

    useEffect(() => {
        performAuthCheck(true);

        const handleStorageChange = (event) => {
            if (['accessToken', 'refreshToken', 'rememberMe'].includes(event.key)) {
                performAuthCheck(false);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [performAuthCheck]);

    // ✅ OPTIMIZATION: Memoize login function
    const login = useCallback(async (userData, rememberMe = false) => {
        localStorage.setItem('accessToken', userData.accessToken);
        localStorage.setItem('refreshToken', userData.refreshToken);
        localStorage.setItem('rememberMe', rememberMe.toString());
        if (userData.user?.companySlug) {
            localStorage.setItem('companySlug', userData.user.companySlug);
        }

        const processedPermissions = processPermissions(userData.user.permissions);

        const initialAuthState = {
            id: userData.user.id,
            userId: userData.user.id,
            name: userData.user.name,
            email: userData.user.email,
            role: userData.user.role,
            position: userData.user.position,
            status: userData.user.status,
            employeeId: userData.user.employeeId,
            profileImage: userData.user.profileImage,
            cardBackground: userData.user.cardBackground,
            projects: userData.user.projects,
            awards: userData.user.awards,
            token: userData.accessToken,
            rememberMe,
            companySlug: userData.user.companySlug,
            companyName: userData.user.companyName,
            permissions: processedPermissions,
            managedUsers: userData.user.managedUsers || [],
            isManager: userData.user.isManager || false,
            hasPermission: (tabName, featureName) => {
                return Boolean(processedPermissions.features?.[tabName]?.[featureName]);
            },
            canAccessTab: (tabName) => {
                return processedPermissions.sidebar.includes(tabName);
            },
        };

        setAuth(initialAuthState);

        try {
            const completeUserData = await apiCallWithAuth('/api/auth/me');
            
            // Store companySlug in localStorage for API calls
            if (completeUserData.companySlug) {
                localStorage.setItem('companySlug', completeUserData.companySlug);
            }
            
            const completeProcessedPermissions = processPermissions(completeUserData.permissions);

            setAuth((prev) => ({
                ...prev,
                ...completeUserData,
                companySlug: completeUserData.companySlug,
                cardBackground: completeUserData.cardBackground,
                permissions: completeProcessedPermissions,
                managedUsers: completeUserData.managedUsers || [],
                isManager: completeUserData.isManager || false,
                hasPermission: (tabName, featureName) => {
                    return Boolean(completeProcessedPermissions.features?.[tabName]?.[featureName]);
                },
                canAccessTab: (tabName) => {
                    return completeProcessedPermissions.sidebar.includes(tabName);
                }
            }));
        } catch (error) {
            console.error('Error fetching complete user data:', error);
            // Don't fail login if this fails - user already has basic auth
        }
    }, [apiCallWithAuth, processPermissions]);

    // ✅ OPTIMIZATION: Memoize logout function
    const logout = useCallback(async () => {
        try {
            await apiCallWithAuth('/api/auth/logout', { method: 'POST' });
        } catch (error) {
            console.error('Logout API call failed:', error);
            // Continue with logout even if API call fails
        }
        setAuth(null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('companySlug');
        window.location.href = '/sign-in';
    }, [apiCallWithAuth]);

    // ✅ Auto-refresh token — runs at 50min so token (1h) never expires mid-session
    useEffect(() => {
        let refreshInterval;

        if (auth) {
            refreshInterval = setInterval(async () => {
                try {
                    await refreshAccessToken();
                } catch (error) {
                    console.error('Auto-refresh failed:', error);
                    // Don't logout on auto-refresh failure if it's a service error
                    const isServiceError = error.status === 503 ||
                        error.code === 'DB_NOT_CONNECTED';
                    if (!isServiceError) {
                        // Only logout on auth errors
                        logout();
                    }
                }
            }, 50 * 60 * 1000); // 50 minutes — token expires at 60min
        }

        return () => {
            if (refreshInterval) clearInterval(refreshInterval);
        };
    }, [auth, refreshAccessToken, ensureServiceHealth, logout]);

    // ✅ CRITICAL OPTIMIZATION: Memoize context value
    const contextValue = useMemo(() => ({
        auth,
        setAuth,
        login,
        logout,
        isAuthenticated: !!auth,
        loading,
        apiCall: apiCallWithAuth,
        refreshAccessToken,
        hasPermission,
        canAccessTab,
        isManager,
        getManagedUsers,
        refreshAuth: () => performAuthCheck(false),
        checkPermission: hasPermission,
        hasTabAccess: canAccessTab,
        ensureServiceHealth,
    }), [
        auth,
        login,
        logout,
        loading,
        apiCallWithAuth,
        refreshAccessToken,
        hasPermission,
        canAccessTab,
        isManager,
        getManagedUsers,
        performAuthCheck,
        ensureServiceHealth
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;